// Recorded ambient (Kevin MacLeod, "Western Streets", CC-BY 4.0) +
// recorded whip-crack SFX (ElevenLabs) + procedural Web Audio fallbacks.
// Audio context is created lazily on first user gesture to satisfy browser
// autoplay policies.

let _ctx = null;
let _enabled = (() => {
  try { return localStorage.getItem("smokehouse_audio") !== "off"; } catch { return true; }
})();
let _ambientEl = null;
let _whipBuf = null;
let _whipLoading = false;

const AMBIENT_SRC = "/audio-western.mp3";
const WHIP_SRC = "/whip-crack.mp3";

const ctx = () => {
  if (!_ctx) {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    try { _ctx = new Ctor(); } catch { return null; }
  }
  if (_ctx.state === "suspended") _ctx.resume().catch(() => {});
  return _ctx;
};

// Lazy-decode the whip-crack MP3 into an AudioBuffer the first time it's
// needed. Subsequent plays are instantaneous — no fetch, no decode.
const ensureWhipBuffer = () => {
  if (_whipBuf || _whipLoading) return;
  const c = ctx(); if (!c) return;
  _whipLoading = true;
  fetch(WHIP_SRC)
    .then((r) => r.arrayBuffer())
    .then((ab) => c.decodeAudioData(ab))
    .then((buf) => { _whipBuf = buf; })
    .catch(() => {})
    .finally(() => { _whipLoading = false; });
};

const playWhip = (gainLevel = 0.9, playbackRate = 1.0) => {
  const c = ctx(); if (!c) return false;
  if (!_whipBuf) { ensureWhipBuffer(); return false; }
  const src = c.createBufferSource();
  src.buffer = _whipBuf;
  src.playbackRate.value = playbackRate;
  const g = c.createGain();
  g.gain.value = gainLevel;
  src.connect(g).connect(c.destination);
  src.start();
  return true;
};

export const isAudioEnabled = () => _enabled;
export const setAudioEnabled = (on) => {
  _enabled = on;
  try { localStorage.setItem("smokehouse_audio", on ? "on" : "off"); } catch {}
  if (!on) stopAmbient();
};

const env = (gain, t0, attack, hold, release, peak = 0.6) => {
  gain.gain.cancelScheduledValues(t0);
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peak, t0 + attack);
  gain.gain.linearRampToValueAtTime(peak * 0.85, t0 + attack + hold);
  gain.gain.linearRampToValueAtTime(0, t0 + attack + hold + release);
};

export const playSplat = () => {
  if (!_enabled) return;
  const c = ctx(); if (!c) return;
  const buf = c.createBuffer(1, c.sampleRate * 0.3, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  const src = c.createBufferSource(); src.buffer = buf;
  const filter = c.createBiquadFilter(); filter.type = "lowpass"; filter.frequency.value = 1200;
  const gain = c.createGain();
  src.connect(filter).connect(gain).connect(c.destination);
  const now = c.currentTime;
  env(gain, now, 0.005, 0.02, 0.25, 0.5);
  src.start(now); src.stop(now + 0.3);
};

export const playDraw = () => {
  if (!_enabled) return;
  // Recorded whip-crack at full volume — the SLURP! moment.
  if (playWhip(1.0, 1.0)) return;

  // Fallback: synthesized whip-crack if MP3 hasn't decoded yet on first use.
  const c = ctx(); if (!c) return;
  const now = c.currentTime;
  const duration = 0.35;
  const sr = c.sampleRate;
  const buf = c.createBuffer(1, sr * duration, sr);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 1.5);
  }
  const src = c.createBufferSource(); src.buffer = buf;
  const bp = c.createBiquadFilter(); bp.type = "bandpass"; bp.Q.value = 7;
  bp.frequency.setValueAtTime(800, now);
  bp.frequency.exponentialRampToValueAtTime(5000, now + 0.04);
  bp.frequency.exponentialRampToValueAtTime(2500, now + duration);
  const hp = c.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 600;
  const gain = c.createGain();
  src.connect(bp).connect(hp).connect(gain).connect(c.destination);
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.9, now + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.25, now + 0.06);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  src.start(now); src.stop(now + duration);
};

export const playTick = () => {
  if (!_enabled) return;
  // Same recorded whip-crack as the DRAW SLURP! — quieter and slightly
  // pitched up so UI clicks stay zippy without competing with the main beat.
  if (playWhip(0.5, 1.2)) return;

  // Fallback synth (only used until the MP3 finishes decoding).
  const c = ctx(); if (!c) return;
  const now = c.currentTime;
  const duration = 0.18;
  const sr = c.sampleRate;
  const buf = c.createBuffer(1, sr * duration, sr);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2);
  }
  const src = c.createBufferSource(); src.buffer = buf;
  const bp = c.createBiquadFilter(); bp.type = "bandpass"; bp.Q.value = 8;
  bp.frequency.setValueAtTime(1000, now);
  bp.frequency.exponentialRampToValueAtTime(4200, now + 0.03);
  bp.frequency.exponentialRampToValueAtTime(2200, now + duration);
  const hp = c.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 700;
  const gain = c.createGain();
  src.connect(bp).connect(hp).connect(gain).connect(c.destination);
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.55, now + 0.003);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  src.start(now); src.stop(now + duration);
};

export const playWin = () => {
  if (!_enabled) return;
  const c = ctx(); if (!c) return;
  const now = c.currentTime;
  [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
    const o = c.createOscillator(); o.type = "triangle"; o.frequency.value = f;
    const g = c.createGain();
    o.connect(g).connect(c.destination);
    env(g, now + i * 0.08, 0.01, 0.06, 0.18, 0.3);
    o.start(now + i * 0.08); o.stop(now + i * 0.08 + 0.3);
  });
};

export const playLose = () => {
  if (!_enabled) return;
  const c = ctx(); if (!c) return;
  const now = c.currentTime;
  [392, 311.13, 246.94].forEach((f, i) => {
    const o = c.createOscillator(); o.type = "sawtooth"; o.frequency.value = f;
    const filter = c.createBiquadFilter(); filter.type = "lowpass"; filter.frequency.value = 800;
    const g = c.createGain();
    o.connect(filter).connect(g).connect(c.destination);
    env(g, now + i * 0.12, 0.01, 0.1, 0.3, 0.18);
    o.start(now + i * 0.12); o.stop(now + i * 0.12 + 0.45);
  });
};

export const playFoul = () => {
  if (!_enabled) return;
  const c = ctx(); if (!c) return;
  const o = c.createOscillator(); o.type = "square"; o.frequency.value = 110;
  const g = c.createGain();
  o.connect(g).connect(c.destination);
  const now = c.currentTime;
  env(g, now, 0.005, 0.15, 0.2, 0.3);
  o.start(now); o.stop(now + 0.4);
};

// ---- Recorded ambient loop ----
export const startAmbient = () => {
  if (!_enabled) return;
  if (_ambientEl) return;
  try {
    const el = new Audio(AMBIENT_SRC);
    el.loop = true;
    el.volume = 0.32;
    el.preload = "auto";
    const p = el.play();
    if (p && p.catch) p.catch(() => { /* autoplay blocked, ignore */ });
    _ambientEl = el;
  } catch {}
};

export const stopAmbient = () => {
  if (_ambientEl) {
    try { _ambientEl.pause(); _ambientEl.src = ""; } catch {}
    _ambientEl = null;
  }
};

// Arm a one-shot listener that starts the ambient loop on the first user
// gesture anywhere on the document. Browsers block autoplay until this
// happens. Safe to call multiple times.
let _armed = false;
export const armAutoStart = () => {
  if (_armed) return;
  _armed = true;
  const kick = () => {
    // Wake the audio context (mobile autoplay policy) and prefetch the
    // whip-crack buffer so the very first SLURP plays the recorded sample.
    ctx();
    ensureWhipBuffer();
    if (_enabled) startAmbient();
    document.removeEventListener("pointerdown", kick);
    document.removeEventListener("keydown", kick);
    document.removeEventListener("touchstart", kick);
  };
  document.addEventListener("pointerdown", kick, { once: true });
  document.addEventListener("keydown", kick, { once: true });
  document.addEventListener("touchstart", kick, { once: true });
};
